import type { Marker } from '@sembo-private/maps';
import type { Cluster } from '@googlemaps/markerclusterer';

import { i18n } from '../localization/localizationSetup';

export enum MarkerType {
  Destination = 'destination-marker',
  Hotel = 'hotel-marker',
  Location = 'location-marker',
  Activity = 'activity-marker',
  Golf = 'golf-marker',
  Flight = 'flight-marker',
  Ferry = 'ferry-marker',
  DetailsAccommodation = 'details-accommodation-marker',
  DetailsActivity = 'details-activity-marker',
  DetailsGolf = 'details-golf-marker',
}

const { t } = i18n.global;

export type MarkerFormatter =
  | Record<MarkerType, (marker: Marker) => HTMLDivElement>
  | Record<MarkerType, string>;

export const markerFormattersHTML: Record<
  MarkerType,
  (marker: Marker) => HTMLDivElement
> = {
  [MarkerType.Destination]: (marker) => {
    const div = document.createElement('div');

    div.className = 'trip-planner-destination-marker';

    div.id = `destination-${marker.data?.ingredientId}`;

    div.innerHTML = `
        <div class="map-marker-icon"></div>
        <div class="map-marker-content">
          <div class="map-marker-title">${marker.title}</div>
          <div class="map-marker-subtitle">${marker.subtitle}</div>
        </div>
        <div class="map-marker-arrow"></div>
    `;

    return div;
  },
  [MarkerType.Hotel]: (marker) => {
    const div = document.createElement('div');

    div.className = `trip-planner-hotel-marker ${marker.cssClass || ''}`;

    div.innerHTML = `
    <div class="map-marker-hotel-title">
      <span>${marker.data?.formattedPrice}</span>
    </div>
  `;

    return div;
  },
  [MarkerType.Location]: () => {
    const div = document.createElement('div');

    div.className = 'trip-planner-location-marker';

    return div;
  },
  [MarkerType.Activity]: (marker) => {
    const div = document.createElement('div');

    div.className = `trip-planner-activity-marker ${marker.cssClass || ''}`;

    div.innerHTML = `
      <div class="activity-marker-icon"></div>
      <div class="map-marker-content">
        <div class="map-marker-title">
          <span>${marker.data?.formattedPrice}</span>
        </div>
      </div>
  `;

    return div;
  },
  [MarkerType.Golf]: (marker) => {
    const div = document.createElement('div');

    div.className = `trip-planner-activity-marker ${marker.cssClass || ''}`;

    div.innerHTML = `
      <div class="golf-marker-icon"></div>
      <div class="map-marker-content">
        <div class="map-marker-title">
          <span>${marker.data?.formattedPrice}</span>
        </div>
      </div>
    `;

    return div;
  },
  [MarkerType.Flight]: () => {
    const div = document.createElement('div');

    div.className = 'trip-planner-flight-marker';

    return div;
  },
  [MarkerType.Ferry]: () => {
    const div = document.createElement('div');

    div.className = 'trip-planner-ferry-marker';

    return div;
  },
  [MarkerType.DetailsAccommodation]: (marker) => {
    const div = document.createElement('div');

    div.className = `trip-planner-details-accommodation-marker ${
      marker.cssClass || ''
    }`;
    div.innerHTML = `<img src="https://images.sembo.travel/trip-planner/Bed.png">`;
    return div;
  },
  [MarkerType.DetailsActivity]: (marker) => {
    const div = document.createElement('div');

    div.className = `trip-planner-details-activity-marker ${
      marker.cssClass || ''
    }`;
    div.innerHTML = `<img src="https://images.sembo.travel/trip-planner/Ticket.png">`;
    return div;
  },
  [MarkerType.DetailsGolf]: (marker) => {
    const div = document.createElement('div');
    div.className = `trip-planner-details-golf-marker ${marker.cssClass || ''}`;
    div.innerHTML = `<img src="https://images.sembo.travel/trip-planner/GolfMarker.png">`;
    return div;
  },
};

export const clusterMarkerFormattersHTML: Record<
  MarkerType,
  (marker: Marker, cluster: Cluster) => HTMLDivElement
> = {
  [MarkerType.Destination]: () => document.createElement('div'),
  [MarkerType.Hotel]: (marker, cluster) => {
    const div = document.createElement('div');

    div.className = `trip-planner-hotel-marker ${marker.cssClass || ''}`;

    div.innerHTML = `
    <div class="map-marker-hotel-title">
      <span> ${cluster.count} ${t('planner.properties')} </span>
    </div>
  `;

    return div;
  },
  [MarkerType.Location]: () => document.createElement('div'),
  [MarkerType.Activity]: (marker, cluster) => {
    const div = document.createElement('div');

    div.className = `trip-planner-activity-marker ${marker.cssClass || ''}`;

    div.innerHTML = `
    <div class="map-marker-activity-title">
      <span> ${cluster.count} ${t('planner.activities')} </span>
    </div>
  `;

    return div;
  },
  [MarkerType.Golf]: (marker, cluster) => {
    const div = document.createElement('div');

    div.className = `trip-planner-golf-marker ${marker.cssClass || ''}`;

    div.innerHTML = `
    <div class="map-marker-golf-title">
      <span> ${cluster.count} ${t('planner.activities')} </span>
    </div>
  `;

    return div;
  },
  [MarkerType.Flight]: () => document.createElement('div'),
  [MarkerType.Ferry]: () => document.createElement('div'),
  [MarkerType.DetailsAccommodation]: () => document.createElement('div'),
  [MarkerType.DetailsActivity]: () => document.createElement('div'),
  [MarkerType.DetailsGolf]: () => document.createElement('div'),
};

export const markerFormattersURL: Partial<Record<MarkerType, string>> = {
  [MarkerType.DetailsAccommodation]:
    'https://images.sembo.travel/trip-planner/Bed.png',
  [MarkerType.DetailsActivity]:
    'https://images.sembo.travel/trip-planner/Ticket.png',
  [MarkerType.DetailsGolf]:
    'https://images.sembo.travel/trip-planner/GolfMarker.png',
};
